import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import ArrowUpIcon from "assets/icons/ArrowUpIcon";
import ArrowDownIcon from "assets/icons/ArrowDownIcon";
import type { Class } from "types/Class";

interface ClassYearDropdownProps {
  classes: Class[];
  currentClass: Class | undefined;
  onChange: (classId: number) => void;
  classYear: number;
  className?: string;
}

const ClassYearDropdown: React.FC<ClassYearDropdownProps> = ({
  classes,
  currentClass,
  onChange,
  classYear,
  className = "",
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter classes by the class year and sort them in Japanese aiueo order
  const filteredAndSortedClasses = classes
    .filter((c) => c.year === classYear)
    .sort((a, b) => a.name.localeCompare(b.name, "ja"));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && currentClass) {
      const valueElement = document.getElementById(`class_${currentClass.id}`);
      valueElement?.scrollIntoView({ behavior: "auto", block: "center" });
    }
  }, [isOpen, currentClass]);

  return (
    <div
      ref={dropdownRef}
      className={`w-full h-full space-y-1 relative cursor-pointer ${className || ""}`}
    >
      <div
        className="w-full h-full flex items-stretch bg-white rounded-lg border border-[#9B9B9B] overflow-hidden"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex min-w-0 flex-1 items-center p-3">
          <p className="truncate">
            {currentClass
              ? `${currentClass.year} - ${currentClass.name}`
              : t("_published_picture._class")}
          </p>
        </div>
        <div className="dropdown-chevron-segment shrink-0">
          <span
            aria-hidden="true"
            className="dropdown-chevron-divider"
          />
          {isOpen ? (
            <ArrowUpIcon width={24} height={24} color={"#767676"} />
          ) : (
            <ArrowDownIcon width={24} height={24} color={"#767676"} />
          )}
        </div>
      </div>
      {isOpen && (
        <ul className="w-full bg-white rounded-lg p-2 max-h-[400px] overflow-auto absolute z-40 list-none border border-[#9B9B9B] shadow-lg">
          {filteredAndSortedClasses.map((c) => (
            <li
              key={c.id}
              id={`class_${c.id}`}
              className={`p-2 cursor-pointer ${
                currentClass?.id === c.id ? "bg-[#F4F4F4]" : ""
              } hover:bg-[#F4F4F4]`}
              onClick={() => {
                onChange(c.id);
                setIsOpen(false);
              }}
            >
              {`${c.year} - ${c.name}`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ClassYearDropdown;
